import { FormEvent, ReactElement, useState } from 'react';

import axios from 'axios';

import { useUpdateDependentForm } from '../../../../../contexts/UpdateDependentFormProviderGdf';
import { usePlansDataStore } from '../../../../../stores/usePlansDataStore';
import { Input } from '../../../../Input';
import { GoForwardButton } from '../../../GoForwardButton';
import { AssociadoSchema } from '../../../data/schemaDependent/AssociadoSchema';
import { formatCurrencyBRL } from '../../../../../utils/FormatarValor';

const Prefeitura_Ilha_cod = Number(
  process.env.REACT_APP_CD_PREFEITURA_ILHABELA ?? '28229',
);
const Prefeitura_Ilha_cod_dep = Number(
  process.env.REACT_APP_CD_PREFEITURA_ILHABELA_DEP ?? '28236',
);
const PLANO_EMPRESA_REMAP: Record<number, number> = {
  [Prefeitura_Ilha_cod]: Prefeitura_Ilha_cod_dep,
};

const PLANOS_LIBERADOS = [124, 217, 251];
const SITUACOES_ATIVAS = [1, 16, 17, 19];

type DependenteApi = {
  nomeDependente: string;
  sexo: string;
  numeroCpfDependente?: string;
  codigoGrauParentesco: number;
  codigoPlano: number;
  valorPlano?: string;
  dataNascimento?: string;
  codigoSituacao?: number;
};

type AssociadoApi = {
  codigo: number;
  codigoDaEmpresa: number;
  codigoContratoDependente?: number;
  nomeDepartamento?: string;
  codigoDepartamento?: number;
  matricula: string;
  dependentes: DependenteApi[];
};

type PlanoApi = {
  Id: number;
  Nome?: string;
  ValorTitular: string;
  ValorDependente?: string;
};

function normalizeText(value = ''): string {
  return value
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .toUpperCase();
}

function normalizeCpf(value?: string): string {
  return value?.replace(/\D/g, '') || '';
}

function getNomePlano(nome?: string, valor?: string): string {
  const normalizedName = normalizeText(nome);
  const valorFormatado = formatCurrencyBRL(valor);

  if (normalizedName.includes('ODONTOCLINICO')) {
    return `OdontoClinico - Somente ${valorFormatado} por vida`;
  }

  if (normalizedName.includes('ODONTO ORTO PROTESE')) {
    return `OdontoProtese - Somente ${valorFormatado} por vida`;
  }

  if (normalizedName.includes('ODONTO ORTO')) {
    return `OdontoOrto - Somente ${valorFormatado} por vida`;
  }

  return '';
}

function formatBirthDate(value?: string): string {
  if (!value) {
    return '';
  }

  const dateOnly = value.split(' ')[0].trim();
  const isoDateMatch = dateOnly.match(/^(\d{4})-(\d{2})-(\d{2})$/);

  if (isoDateMatch) {
    const [, year, month, day] = isoDateMatch;
    return `${day}/${month}/${year}`;
  }

  const slashDateMatch = dateOnly.match(/^(\d{2})\/(\d{2})\/(\d{4})$/);

  if (slashDateMatch) {
    const [, first, second, year] = slashDateMatch;

    if (Number(second) > 12) {
      return `${second}/${first}/${year}`;
    }

    return dateOnly;
  }

  return dateOnly;
}

function validateAssociado(obj: unknown): boolean {
  if (!obj || typeof obj !== 'object') {
    return false;
  }

  const result = AssociadoSchema.safeParse({
    ...(obj as Record<string, unknown>),
    dependentes: undefined,
  });

  if (result.success === false) {
    return false;
  }

  return true;
}

export function ServidorValidationForm(): ReactElement {
  const { register, handleSubmit, goToNextForm, errors, watch, setValue } =
    useUpdateDependentForm();

  const [error, setError] = useState<string | null>(null);
  const [showError, setShowError] = useState(false);

  const CPF = watch('Associado.cpf');
  const setPlansData = usePlansDataStore(x => x.setPlansData);

  const showTemporaryError = (message: string) => {
    setError(message);
    setShowError(true);

    setTimeout(() => {
      setShowError(false);
    }, 5000);
  };

  const fillAssociadoForm = (Associado: any) => {
    const cpfAssociado = normalizeCpf(Associado.cpf || CPF);
    const titulares = Associado.dependentes.filter(
      x => Number(x.codigoGrauParentesco) === 1,
    );
    const titular =
      titulares.find(
        x =>
          normalizeCpf(x.numeroCpfDependente) &&
          normalizeCpf(x.numeroCpfDependente) === cpfAssociado,
      ) ?? titulares[0];
    const dependentes = Associado.dependentes.filter(x => x !== titular);

    if (!titular) {
      return;
    }

    setValue('Associado.id', Associado.codigo);
    setValue('Associado.name', titular.nomeDependente);
    setValue('Associado.plano', String(titular.codigoPlano));
    setValue('Associado.plano_vl', titular.valorPlano);
    setValue(
      'Associado.departamento',
      Associado.nomeDepartamento || 'Sem Departamento',
    );
    setValue('Associado.departamentoId', Associado.codigoDepartamento || 0);
    setValue('Associado.matricula', Associado.matricula ?? '');
    setValue('Associado.birthDate', formatBirthDate(titular.dataNascimento));
    setValue('Associado.selecionado', Associado);
    setValue('Associado.dependentes', []);

    dependentes.forEach((dependente, index) => {
      setValue(
        `Associado.dependentes.${index}.name`,
        dependente.nomeDependente,
      );
      setValue(`Associado.dependentes.${index}.gender`, dependente.sexo);
      setValue(
        `Associado.dependentes.${index}.parentage`,
        dependente.codigoGrauParentesco,
      );
      setValue(`Associado.dependentes.${index}.plano`, dependente.codigoPlano);
      setValue(
        `Associado.dependentes.${index}.plano_vl`,
        dependente.valorPlano,
      );
    });
  };

  const fetchPlans = async (empresaId: number) => {
    const token = process.env.REACT_APP_TOKEN_S4E_V2;
    const urlBase = process.env.REACT_APP_URL_BASE;
    const idEmpresa = PLANO_EMPRESA_REMAP[empresaId] ?? empresaId;
    const response = await axios.get(
      `${urlBase}/api/plano/planos?token=${token}&empresaId=${idEmpresa}`,
    );

    if (
      response.status !== 200 ||
      response.data.codigo !== 1 ||
      !Array.isArray(response.data.dados) ||
      response.data.dados.length === 0
    ) {
      throw new Error(
        'Nao foi possivel obter os planos para a empresa selecionada.',
      );
    }

    const planosData = response.data.dados
      .filter((plano: PlanoApi) => PLANOS_LIBERADOS.includes(plano.Id))
      .map((plano: PlanoApi) => ({
        Id: plano.Id,
        Nome: getNomePlano(plano.Nome, plano.ValorTitular),
        Valor: plano.ValorTitular,
        valor: plano.ValorDependente ?? plano.ValorTitular,
      }));

    setPlansData(planosData);
  };

  const getCPFDetails = async (cpf: string) => {
    const formatedCpf = cpf.replace(/[.\-\s]/g, '');
    const token = process.env.REACT_APP_TOKEN_S4E_V2;
    const response = await fetch(
      `${process.env.REACT_APP_GET_ASSOCIADO_CPF}?token=${token}&cpfAssociado=${formatedCpf}&limite=20`,
    );

    if (!response.ok) {
      throw new Error('Nao foi possivel consultar o associado.');
    }

    const getAssociado = await response.json();
    const associados: AssociadoApi[] = Array.isArray(getAssociado.dados)
      ? getAssociado.dados
      : [];

    const ativo = associados.filter(associado =>
      associado.dependentes?.some(dependente =>
        SITUACOES_ATIVAS.includes(Number(dependente.codigoSituacao ?? 0)),
      ),
    );

    const associadoGdf = ativo.find(
      associado => Number(associado.codigoDaEmpresa) === 27855,
    );

    if (associadoGdf) {
      return associadoGdf;
    }

    const associadoTitularEmpresa = ativo.find(
      associado => Number(associado.codigoDaEmpresa) === Prefeitura_Ilha_cod,
    );
    if (!associadoTitularEmpresa) {
      return null;
    }

    const associadoDependenteEmpresa = ativo.find(
      associado =>
        Number(associado.codigoDaEmpresa) === Prefeitura_Ilha_cod_dep,
    );

    if (!associadoDependenteEmpresa) {
      return associadoTitularEmpresa;
    }
    return {
      ...associadoTitularEmpresa,
      codigoContratoDependente: associadoDependenteEmpresa.codigo,
      dependentes: [
        ...(associadoTitularEmpresa.dependentes ?? []),
        ...(associadoDependenteEmpresa.dependentes ?? []),
      ],
    };
  };

  const validaDados = async () => {
    if (!CPF) {
      handleSubmit(goToNextForm)();
      return false;
    }

    try {
      const Associado = await getCPFDetails(CPF);
      if (Associado) {
        if (Associado.codigoDaEmpresa !== 27855) {
          await fetchPlans(Associado.codigoDaEmpresa);
        }

        fillAssociadoForm(Associado);
      }

      const Validation = watch('Associado');

      if (!validateAssociado(Validation)) {
        showTemporaryError('Associado Nao encontrado');
        return false;
      }

      setError(null);
      handleSubmit(goToNextForm)();
      return true;
    } catch (error) {
      showTemporaryError(
        error instanceof Error
          ? error.message
          : 'Nao foi possivel validar o associado.',
      );

      return false;
    }
  };

  const handleFormSubmit = (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    void validaDados();
  };

  return (
    <form
      action=""
      className="max-w-[766px] w-full"
      onSubmit={handleFormSubmit}
    >
      <h2 className="min-[748px]:text-xl text-lg font-medium text-primary-main mb-8">
        Dados
      </h2>

      <div className="grid min-[748px]:grid-cols-2 grid-cols-1 gap-6">
        <Input
          label="CPF"
          placeholder="CPF"
          activateMask
          mask="999.999.999-99"
          errorMessage={errors.Associado?.cpf?.message}
          {...register('Associado.cpf')}
        />
      </div>

      {showError && (
        <div className="fixed top-24 left-1/2 transform -translate-x-1/5 bg-red-500 text-white py-2 px-4 rounded shadow-md">
          <p className="text-sm font-medium">
            {error?.split('\n').map((line, index) => (
              <span key={index}>
                {line}
                <br />
              </span>
            ))}
          </p>
        </div>
      )}

      <GoForwardButton onForwardClick={validaDados} />
    </form>
  );
}
